import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../store/slice/authSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faBox, faMessage, faCamera, faEdit, faSave, faTimes,
  faExclamationTriangle, faCheckCircle, faMapMarkerAlt, faPhone,
  faBriefcase, faIdCard, faEnvelope, faShieldAlt, faClipboardList,
  faSpinner, faImage, faArrowUp
} from '@fortawesome/free-solid-svg-icons';
import API_CLIENT from '../../lib/api-client';
import { GET_USER_ROUTE, UPDATE_USER_ROUTE, UPLOAD_PROFILE_IMAGE_ROUTE } from '../../utils/constant';
import { toast } from 'react-toastify';

/* ════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════════════════════════ */

const Field = ({ label, name, value, onChange, disabled, required, icon, type = 'text' }) => (
  <div className="profile-field">
    <label className="profile-field__label">
      {label}{required && <span className="profile-field__required">*</span>}
    </label>
    <div className="profile-field__inner">
      {icon && <FontAwesomeIcon icon={icon} className="profile-field__icon" />}
      <input
        type={type}
        name={name}
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        className={`profile-field__input ${icon ? 'profile-field__input--icon' : ''} ${disabled ? 'profile-field__input--disabled' : ''}`}
      />
    </div>
  </div>
);

const StatBadge = ({ label, value, accent }) => (
  <div className="stat-badge" style={{ '--accent': accent }}>
    <span className="stat-badge__value">{value}</span>
    <span className="stat-badge__label">{label}</span>
  </div>
);

/* ════════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════ */
const Profile = () => {
  const { userInfo, setUserInfo } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imgUploading, setImgUploading] = useState(false);
  const [imgPreview, setImgPreview] = useState(null);
  const [orders] = useState([]);
  const [queries] = useState([]);
  const [completionPct, setCompletionPct] = useState(0);
  const [missingFields, setMissingFields] = useState([]);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '', lastName: '', phoneNo: '',
    address: { street: '', city: '', state: '', zipCode: '', country: '' },
    companyName: '', businessLicense: '', gstNumber: '',
  });

  /* ── fetch ─────────────────────────────────────────────────── */
  useEffect(() => { fetchUser(); }, []);

  const syncForm = (u) => setForm({
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    phoneNo: u.phoneNo || '',
    address: u.address || { street: '', city: '', state: '', zipCode: '', country: '' },
    companyName: u.companyName || '',
    businessLicense: u.businessLicense || '',
    gstNumber: u.gstNumber || '',
  });

  const fetchUser = async () => {
    try {
      setLoading(true);
      const { status, data } = await API_CLIENT.get(GET_USER_ROUTE);
      if (status === 200 && data.success) {
        setUserInfo(data.user);
        syncForm(data.user);
        calcCompletion(data.user);
      }
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 403)
        toast.error(err.response?.data?.message || 'Failed to fetch profile');
    } finally { setLoading(false); }
  };

  /* ── completion ─────────────────────────────────────────────── */
  const calcCompletion = (u) => {
    const base = ['firstName', 'lastName', 'phoneNo', 'address'];
    const role = u.role === 'manufacturer' ? ['companyName', 'businessLicense'] : ['gstNumber'];
    const all = [...base, ...role];
    const missing = [];
    let done = 0;
    all.forEach(f => {
      if (f === 'address') {
        ['street', 'city', 'state', 'zipCode', 'country'].every(
          af => u.address?.[af] && String(u.address[af]).trim()
        ) ? done++ : missing.push('Complete Address');
      } else if (u[f] && String(u[f]).trim()) {
        done++;
      } else {
        missing.push(f.replace(/([A-Z])/g, ' $1').trim());
      }
    });
    setCompletionPct(Math.round((done / all.length) * 100));
    setMissingFields(missing);
  };

  useEffect(() => { if (userInfo) calcCompletion(userInfo); }, [userInfo]);

  /* ── input ─────────────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [p, c] = name.split('.');
      setForm(prev => ({ ...prev, [p]: { ...prev[p], [c]: value } }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  /* ── image upload ───────────────────────────────────────────── */
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate client-side
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP or GIF images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    // Show local preview instantly
    const reader = new FileReader();
    reader.onload = (ev) => setImgPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload to Firebase via backend
    try {
      setImgUploading(true);
      const fd = new FormData();
      fd.append('profileImg', file);
      const { status, data } = await API_CLIENT.post(UPLOAD_PROFILE_IMAGE_ROUTE, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (status === 200 && data.success) {
        setUserInfo(data.user);
        setImgPreview(null); // use the server URL from now on
        toast.success('Profile photo updated!');
      }
    } catch (err) {
      setImgPreview(null);
      toast.error(err.response?.data?.message || 'Image upload failed');
    } finally {
      setImgUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /* ── save profile ───────────────────────────────────────────── */
  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        firstName: form.firstName, lastName: form.lastName, phoneNo: form.phoneNo,
        address: form.address,
        ...(userInfo?.role === 'manufacturer'
          ? { companyName: form.companyName, businessLicense: form.businessLicense }
          : { gstNumber: form.gstNumber }),
      };
      const { status, data } = await API_CLIENT.put(UPDATE_USER_ROUTE, payload);
      if (status === 200 && data.success) {
        setUserInfo(data.user);
        calcCompletion(data.user);
        toast.success('Profile updated!');
        setIsEditing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleCancel = () => { syncForm(userInfo); setIsEditing(false); };

  /* ── avatar src ─────────────────────────────────────────────── */
  const avatarSrc = imgPreview
    || userInfo?.profileImg
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo?.userName || 'U')}&background=0d9488&color=fff&size=200`;

  /* ── loading state ──────────────────────────────────────────── */
  if (loading) return (
    <div className="profile-loader">
      <div className="profile-loader__ring" />
      <p className="profile-loader__text">Loading your profile…</p>
    </div>
  );

  const tabs = [
    { id: 'profile', label: 'Profile', icon: faUser },
    { id: 'orders', label: 'Orders', icon: faBox },
    { id: 'queries', label: 'Queries', icon: faMessage },
  ];

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── inline styles (scoped to this page) ─────────────── */}
      <style>{`
        /* ── tokens ── */
        :root{
          --p-teal:    #0d9488;
          --p-teal-d:  #0f766e;
          --p-teal-l:  #ccfbf1;
          --p-teal-xl: #f0fdfa;
          --p-neutral: #f5f5f5;
          --p-card:    #ffffff;
          --p-border:  #e5e7eb;
          --p-text:    #1a1a2e;
          --p-muted:   #6b7280;
          --p-radius:  12px;
          --p-shadow:  0 4px 24px rgba(0,0,0,.07);
        }

        /* ── layout ── */
        .profile-root{
          min-height:100vh;
          background:var(--p-neutral);
          font-family:'Inter',system-ui,sans-serif;
          color:var(--p-text);
        }

        /* ── loader ── */
        .profile-loader{
          min-height:100vh;
          display:flex;flex-direction:column;
          align-items:center;justify-content:center;
          background:var(--p-neutral);
          gap:16px;
        }
        .profile-loader__ring{
          width:48px;height:48px;
          border:4px solid var(--p-teal-l);
          border-top-color:var(--p-teal);
          border-radius:50%;
          animation:spin .8s linear infinite;
        }
        .profile-loader__text{ color:var(--p-muted);font-size:.9rem;letter-spacing:.04em; }
        @keyframes spin{ to{ transform:rotate(360deg); } }

        /* ══════════════════════════════════════════════════════
           HERO BANNER
        ══════════════════════════════════════════════════════ */
        .profile-hero{
          position:relative;
          background:linear-gradient(135deg,#134e4a 0%,#0f766e 50%,#0d9488 100%);
          overflow:hidden;
          padding-bottom:56px;
        }
        /* subtle fabric-weave SVG texture */
        .profile-hero::before{
          content:'';
          position:absolute;inset:0;
          background-image:
            repeating-linear-gradient(0deg,rgba(255,255,255,.04) 0px,rgba(255,255,255,.04) 1px,transparent 1px,transparent 24px),
            repeating-linear-gradient(90deg,rgba(255,255,255,.04) 0px,rgba(255,255,255,.04) 1px,transparent 1px,transparent 24px);
          pointer-events:none;
        }
        /* glowing orbs */
        .profile-hero::after{
          content:'';
          position:absolute;
          width:420px;height:420px;
          border-radius:50%;
          background:radial-gradient(circle,rgba(255,255,255,.08) 0%,transparent 70%);
          top:-120px;right:-80px;
          pointer-events:none;
        }

        .profile-hero__inner{
          position:relative;z-index:1;
          max-width:1100px;margin:0 auto;
          padding:48px 24px 0;
        }

        /* ── avatar area ── */
        .profile-avatar-wrap{
          position:relative;
          width:120px;height:120px;
          flex-shrink:0;
        }
        .profile-avatar{
          width:120px;height:120px;
          border-radius:50%;
          object-fit:cover;
          border:4px solid rgba(255,255,255,.35);
          box-shadow:0 0 0 6px rgba(255,255,255,.1),0 12px 40px rgba(0,0,0,.25);
          transition:opacity .3s;
        }
        .profile-avatar--uploading{ opacity:.5; }
        .profile-avatar-overlay{
          position:absolute;inset:0;
          border-radius:50%;
          background:rgba(0,0,0,.45);
          display:flex;flex-direction:column;
          align-items:center;justify-content:center;
          gap:4px;
          cursor:pointer;
          opacity:0;
          transition:opacity .25s;
        }
        .profile-avatar-wrap:hover .profile-avatar-overlay,
        .profile-avatar-wrap:focus-within .profile-avatar-overlay{ opacity:1; }
        .profile-avatar-overlay span{
          color:#fff;font-size:.65rem;
          letter-spacing:.06em;text-transform:uppercase;font-weight:600;
        }
        .profile-avatar-spinner{
          position:absolute;inset:0;
          border-radius:50%;
          display:flex;align-items:center;justify-content:center;
          background:rgba(0,0,0,.5);
        }
        .profile-avatar-spinner svg{ color:#fff;font-size:1.4rem;animation:spin .7s linear infinite; }

        /* ── hero text ── */
        .profile-hero__meta{
          display:flex;
          flex-direction:column;
          gap:4px;
        }
        .profile-hero__role{
          font-size:.7rem;font-weight:600;
          letter-spacing:.12em;text-transform:uppercase;
          color:rgba(255,255,255,.6);
        }
        .profile-hero__name{
          font-size:2rem;font-weight:300;color:#fff;line-height:1.2;
          margin:0;
        }
        .profile-hero__email{
          font-size:.9rem;color:rgba(255,255,255,.65);
        }
        .profile-hero__badges{
          display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;
        }
        .profile-badge{
          display:inline-flex;align-items:center;gap:6px;
          padding:4px 12px;
          border-radius:999px;
          font-size:.75rem;font-weight:500;
          backdrop-filter:blur(8px);
        }
        .profile-badge--verified{
          background:rgba(255,255,255,.15);
          border:1px solid rgba(255,255,255,.25);
          color:#a7f3d0;
        }
        .profile-badge--pending{
          background:rgba(245,158,11,.15);
          border:1px solid rgba(245,158,11,.3);
          color:#fcd34d;
        }
        .profile-badge--role{
          background:rgba(255,255,255,.12);
          border:1px solid rgba(255,255,255,.2);
          color:rgba(255,255,255,.85);
          text-transform:capitalize;
        }

        /* ── completion arc ── */
        .profile-completion{
          min-width:140px;text-align:center;
        }
        .profile-completion__arc{
          position:relative;
          width:80px;height:80px;
          margin:0 auto 8px;
        }
        .profile-completion__svg{ transform:rotate(-90deg); }
        .profile-completion__track{ fill:none;stroke:rgba(255,255,255,.15);stroke-width:6; }
        .profile-completion__fill{
          fill:none;stroke:#fff;stroke-width:6;
          stroke-linecap:round;
          stroke-dasharray:226;
          transition:stroke-dashoffset 1s ease;
        }
        .profile-completion__label{
          position:absolute;inset:0;
          display:flex;align-items:center;justify-content:center;
          font-size:1rem;font-weight:600;color:#fff;
        }
        .profile-completion__sub{
          font-size:.7rem;color:rgba(255,255,255,.6);letter-spacing:.05em;text-transform:uppercase;
        }

        /* ── hero flex layout ── */
        .profile-hero__row{
          display:flex;
          align-items:flex-start;
          gap:24px;
          flex-wrap:wrap;
          justify-content:space-between;
        }
        .profile-hero__left{
          display:flex;align-items:center;gap:20px;flex-wrap:wrap;
        }

        /* ── stat chips ── */
        .profile-stats{
          display:flex;gap:12px;flex-wrap:wrap;
          padding-top:8px;
        }
        .stat-badge{
          background:rgba(255,255,255,.1);
          border:1px solid rgba(255,255,255,.18);
          border-radius:10px;
          padding:10px 16px;
          backdrop-filter:blur(6px);
          display:flex;flex-direction:column;align-items:center;
          min-width:70px;
        }
        .stat-badge__value{
          font-size:1.2rem;font-weight:300;color:#fff;
        }
        .stat-badge__label{
          font-size:.65rem;text-transform:uppercase;
          letter-spacing:.08em;color:rgba(255,255,255,.55);margin-top:2px;
        }

        /* ══════════════════════════════════════════════════════
           TABS
        ══════════════════════════════════════════════════════ */
        .profile-tabs-bar{
          background:#fff;
          border-bottom:1px solid var(--p-border);
          position:sticky;top:0;z-index:20;
          box-shadow:0 1px 8px rgba(0,0,0,.04);
        }
        .profile-tabs-inner{
          max-width:1100px;margin:0 auto;
          padding:0 24px;
          display:flex;gap:4px;
          overflow-x:auto;
        }
        .profile-tab{
          display:flex;align-items:center;gap:8px;
          padding:14px 22px;
          font-size:.85rem;font-weight:500;
          border:none;background:none;cursor:pointer;
          border-bottom:2px solid transparent;
          color:var(--p-muted);
          transition:color .2s,border-color .2s;
          white-space:nowrap;
        }
        .profile-tab--active{
          border-bottom-color:var(--p-teal);
          color:var(--p-teal);
        }
        .profile-tab:hover:not(.profile-tab--active){ color:#374151; }

        /* ══════════════════════════════════════════════════════
           CONTENT AREA
        ══════════════════════════════════════════════════════ */
        .profile-content{
          max-width:1100px;margin:0 auto;
          padding:32px 24px 60px;
        }

        /* incomplete banner */
        .profile-banner{
          background:linear-gradient(135deg,#fffbeb,#fef3c7);
          border:1px solid #fcd34d;
          border-radius:var(--p-radius);
          padding:14px 20px;
          display:flex;align-items:flex-start;gap:12px;
          margin-bottom:24px;
        }
        .profile-banner__icon{ color:#f59e0b;margin-top:2px;flex-shrink:0; }
        .profile-banner__title{ font-size:.85rem;font-weight:600;color:#92400e; }
        .profile-banner__text{ font-size:.82rem;color:#b45309;margin-top:2px; }

        /* section header */
        .section-head{
          display:flex;align-items:center;justify-content:space-between;
          margin-bottom:24px;
        }
        .section-head__left{ display:flex;flex-direction:column;gap:2px; }
        .section-head__tag{
          font-size:.68rem;font-weight:600;
          letter-spacing:.14em;text-transform:uppercase;
          color:var(--p-teal);
        }
        .section-head__title{
          font-size:1.4rem;font-weight:300;color:#111827;margin:0;
        }
        .section-head__line{
          width:32px;height:2px;background:var(--p-teal);margin-top:4px;
          transition:width .4s;
        }
        .section-head__actions{ display:flex;gap:10px;align-items:center; }

        /* action buttons */
        .btn{
          display:inline-flex;align-items:center;gap:8px;
          padding:9px 20px;border-radius:6px;
          font-size:.82rem;font-weight:500;cursor:pointer;
          transition:all .2s;border:1px solid transparent;
        }
        .btn--primary{
          background:var(--p-teal);color:#fff;border-color:var(--p-teal);
        }
        .btn--primary:hover{ background:var(--p-teal-d);border-color:var(--p-teal-d); }
        .btn--primary:disabled{ opacity:.6;cursor:not-allowed; }
        .btn--ghost{
          background:transparent;color:#6b7280;border-color:#d1d5db;
        }
        .btn--ghost:hover{ color:#374151;border-color:#9ca3af; }

        /* ── cards ── */
        .profile-card{
          background:var(--p-card);
          border:1px solid var(--p-border);
          border-radius:var(--p-radius);
          padding:28px;
          box-shadow:var(--p-shadow);
          transition:box-shadow .25s;
        }
        .profile-card:hover{ box-shadow:0 8px 32px rgba(0,0,0,.1); }
        .profile-card + .profile-card{ margin-top:20px; }

        .profile-card__header{
          display:flex;align-items:center;gap:12px;
          margin-bottom:22px;
          padding-bottom:16px;
          border-bottom:1px solid #f3f4f6;
        }
        .profile-card__icon{
          width:36px;height:36px;border-radius:8px;
          display:flex;align-items:center;justify-content:center;
          font-size:.85rem;flex-shrink:0;
        }
        .profile-card__icon--teal{ background:var(--p-teal-l);color:var(--p-teal-d); }
        .profile-card__icon--purple{ background:#ede9fe;color:#7c3aed; }
        .profile-card__icon--amber{ background:#fef3c7;color:#d97706; }
        .profile-card__title{ font-size:.95rem;font-weight:600;color:#111827; }
        .profile-card__sub{ font-size:.78rem;color:var(--p-muted);margin-top:1px; }

        .profile-grid-2{ display:grid;grid-template-columns:1fr 1fr;gap:16px; }
        .profile-grid-3{ display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px; }
        .col-span-2{ grid-column:span 2; }

        @media(max-width:640px){
          .profile-grid-2,.profile-grid-3{ grid-template-columns:1fr; }
          .col-span-2{ grid-column:span 1; }
        }

        /* ── field ── */
        .profile-field{ display:flex;flex-direction:column;gap:6px; }
        .profile-field__label{
          font-size:.72rem;font-weight:600;
          letter-spacing:.1em;text-transform:uppercase;
          color:var(--p-muted);
        }
        .profile-field__required{ color:var(--p-teal);margin-left:3px; }
        .profile-field__inner{ position:relative; }
        .profile-field__icon{
          position:absolute;left:12px;top:50%;transform:translateY(-50%);
          color:var(--p-teal);font-size:.78rem;pointer-events:none;
        }
        .profile-field__input{
          width:100%;padding:10px 14px;
          border:1px solid #e5e7eb;border-radius:8px;
          font-size:.88rem;color:#111827;
          outline:none;transition:border-color .2s,box-shadow .2s;
          background:#fff;
        }
        .profile-field__input--icon{ padding-left:36px; }
        .profile-field__input--disabled{
          background:#f9fafb;color:#9ca3af;cursor:not-allowed;
        }
        .profile-field__input:not(.profile-field__input--disabled):hover{
          border-color:#9ca3af;
        }
        .profile-field__input:not(.profile-field__input--disabled):focus{
          border-color:var(--p-teal);
          box-shadow:0 0 0 3px rgba(13,148,136,.12);
        }

        /* status tiles */
        .status-tile{
          padding:14px 16px;border-radius:10px;
          display:flex;align-items:center;gap:10px;
          font-size:.83rem;font-weight:500;
        }
        .status-tile--verified{ background:#f0fdfa;color:#0f766e;border:1px solid #99f6e4; }
        .status-tile--pending{ background:#fffbeb;color:#b45309;border:1px solid #fcd34d; }
        .status-tile--role{ background:#f8fafc;color:#475569;border:1px solid #e2e8f0;text-transform:capitalize; }

        /* completion mini bar */
        .completion-row{
          display:flex;align-items:center;gap:12px;
          padding:14px 16px;
          background:var(--p-teal-xl);
          border:1px solid var(--p-teal-l);
          border-radius:10px;
        }
        .completion-row__bar-wrap{
          flex:1;height:6px;background:#ccfbf1;border-radius:999px;overflow:hidden;
        }
        .completion-row__bar{
          height:100%;background:var(--p-teal);border-radius:999px;
          transition:width .8s ease;
        }
        .completion-row__pct{ font-size:.85rem;font-weight:700;color:var(--p-teal-d);min-width:36px;text-align:right; }

        /* ── empty state ── */
        .empty-state{
          display:flex;flex-direction:column;align-items:center;justify-content:center;
          padding:72px 24px;text-align:center;
          gap:12px;
        }
        .empty-state__icon{
          width:72px;height:72px;border-radius:20px;
          display:flex;align-items:center;justify-content:center;
          background:var(--p-neutral);
          font-size:1.8rem;color:#d1d5db;
          margin-bottom:4px;
        }
        .empty-state__title{ font-size:1.1rem;font-weight:300;color:#6b7280; }
        .empty-state__sub{ font-size:.82rem;color:#9ca3af; }

        /* order / query rows */
        .list-row{
          display:flex;align-items:flex-start;justify-content:space-between;gap:12px;
          padding:16px 20px;
          border:1px solid var(--p-border);
          border-radius:10px;
          background:#fff;
          transition:border-color .2s,box-shadow .2s;
        }
        .list-row:hover{ border-color:var(--p-teal);box-shadow:0 4px 16px rgba(13,148,136,.08); }
        .list-row + .list-row{ margin-top:10px; }
        .chip{
          padding:4px 10px;border-radius:999px;
          font-size:.72rem;font-weight:600;letter-spacing:.04em;
        }
        .chip--green{ background:#f0fdfa;color:#0f766e;border:1px solid #99f6e4; }
        .chip--amber{ background:#fffbeb;color:#b45309;border:1px solid #fcd34d; }
        .chip--blue{ background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe; }

        /* misc */
        @media(max-width:720px){
          .profile-hero__name{ font-size:1.5rem; }
          .profile-hero__row{ flex-direction:column;align-items:flex-start; }
          .profile-completion{ min-width:unset;width:100%; }
        }
      `}</style>

      <div className="profile-root">

        {/* ════════════════════════ HERO ════════════════════════ */}
        <div className="profile-hero">
          <div className="profile-hero__inner">
            <div className="profile-hero__row">

              {/* left: avatar + info */}
              <div className="profile-hero__left">
                {/* Avatar */}
                <div className="profile-avatar-wrap">
                  <img
                    src={avatarSrc}
                    alt="Profile"
                    className={`profile-avatar ${imgUploading ? 'profile-avatar--uploading' : ''}`}
                  />
                  {imgUploading ? (
                    <div className="profile-avatar-spinner">
                      <FontAwesomeIcon icon={faSpinner} />
                    </div>
                  ) : (
                    <label className="profile-avatar-overlay" htmlFor="avatarInput">
                      <FontAwesomeIcon icon={faCamera} style={{ color: '#fff', fontSize: '1.2rem' }} />
                      <span>Change</span>
                    </label>
                  )}
                  <input
                    ref={fileInputRef}
                    id="avatarInput"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                </div>

                {/* Info */}
                <div className="profile-hero__meta">
                  <p className="profile-hero__role">{userInfo?.role}</p>
                  <h1 className="profile-hero__name">{userInfo?.userName}</h1>
                  <p className="profile-hero__email">{userInfo?.emailId}</p>
                  <div className="profile-hero__badges">
                    <span className="profile-badge profile-badge--role">
                      <FontAwesomeIcon icon={faBriefcase} style={{ fontSize: '.7rem' }} />
                      {userInfo?.role}
                    </span>
                    <span className={`profile-badge ${userInfo?.isVerified ? 'profile-badge--verified' : 'profile-badge--pending'}`}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '.7rem' }} />
                      {userInfo?.isVerified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                </div>
              </div>

              {/* right: circular completion */}
              <div className="profile-completion">
                <div className="profile-completion__arc">
                  <svg className="profile-completion__svg" width="80" height="80" viewBox="0 0 80 80">
                    <circle className="profile-completion__track" cx="40" cy="40" r="36" />
                    <circle
                      className="profile-completion__fill"
                      cx="40" cy="40" r="36"
                      style={{ strokeDashoffset: 226 - (226 * completionPct) / 100 }}
                    />
                  </svg>
                  <div className="profile-completion__label">{completionPct}%</div>
                </div>
                <p className="profile-completion__sub">Completion</p>
              </div>
            </div>

            {/* stat chips */}
            <div className="profile-stats" style={{ marginTop: 28 }}>
              <StatBadge label="Orders" value={orders.length || '—'} />
              <StatBadge label="Queries" value={queries.length || '—'} />
              <StatBadge label="Status" value={userInfo?.isActive ? 'Active' : 'Inactive'} />
            </div>
          </div>
        </div>

        {/* ════════════════════════ TABS ════════════════════════ */}
        <div className="profile-tabs-bar">
          <div className="profile-tabs-inner">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`profile-tab ${activeTab === t.id ? 'profile-tab--active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                <FontAwesomeIcon icon={t.icon} style={{ fontSize: '.75rem' }} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ════════════════════════ CONTENT ════════════════════════ */}
        <div className="profile-content">

          {/* incomplete banner */}
          {completionPct < 100 && activeTab === 'profile' && (
            <div className="profile-banner">
              <FontAwesomeIcon icon={faExclamationTriangle} className="profile-banner__icon" />
              <div>
                <p className="profile-banner__title">Profile incomplete</p>
                <p className="profile-banner__text">
                  Please fill in: <strong>{missingFields.join(', ')}</strong>
                </p>
              </div>
            </div>
          )}

          {/* ── PROFILE TAB ── */}
          {activeTab === 'profile' && (
            <>
              <div className="section-head">
                <div className="section-head__left">
                  <span className="section-head__tag">Account</span>
                  <h2 className="section-head__title">Profile Information</h2>
                  <div className="section-head__line" />
                </div>
                <div className="section-head__actions">
                  {isEditing && (
                    <button className="btn btn--ghost" onClick={handleCancel}>
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  )}
                  <button
                    className="btn btn--primary"
                    onClick={isEditing ? handleSave : () => setIsEditing(true)}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={saving ? faSpinner : isEditing ? faSave : faEdit}
                      className={saving ? 'fa-spin' : ''} />
                    {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Edit Profile'}
                  </button>
                </div>
              </div>

              {/* Personal Details card */}
              <div className="profile-card">
                <div className="profile-card__header">
                  <div className="profile-card__icon profile-card__icon--teal">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                  <div>
                    <p className="profile-card__title">Personal Details</p>
                    <p className="profile-card__sub">Your name and contact information</p>
                  </div>
                </div>
                <div className="profile-grid-2">
                  <Field label="First Name" name="firstName" value={form.firstName} onChange={handleChange} disabled={!isEditing} required icon={faUser} />
                  <Field label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} disabled={!isEditing} required icon={faUser} />
                  <Field label="Phone Number" name="phoneNo" value={form.phoneNo} onChange={handleChange} disabled={!isEditing} required icon={faPhone} type="tel" />
                  <Field label="Email Address" name="emailId" value={userInfo?.emailId || ''} onChange={() => {}} disabled icon={faEnvelope} />
                </div>
              </div>

              {/* Business Details card */}
              <div className="profile-card">
                <div className="profile-card__header">
                  <div className="profile-card__icon profile-card__icon--purple">
                    <FontAwesomeIcon icon={faBriefcase} />
                  </div>
                  <div>
                    <p className="profile-card__title">Business Details</p>
                    <p className="profile-card__sub">Role-specific business information</p>
                  </div>
                </div>
                <div className="profile-grid-2">
                  {userInfo?.role === 'manufacturer' ? (
                    <>
                      <Field label="Company Name" name="companyName" value={form.companyName} onChange={handleChange} disabled={!isEditing} required icon={faBriefcase} />
                      <Field label="Business License" name="businessLicense" value={form.businessLicense} onChange={handleChange} disabled={!isEditing} required icon={faIdCard} />
                    </>
                  ) : (
                    <Field label="GST Number" name="gstNumber" value={form.gstNumber} onChange={handleChange} disabled={!isEditing} required icon={faIdCard} />
                  )}
                  <div className="status-tile status-tile--role">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    {userInfo?.role}
                  </div>
                  <div className={`status-tile ${userInfo?.isVerified ? 'status-tile--verified' : 'status-tile--pending'}`}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {userInfo?.isVerified ? 'Verified Account' : 'Pending Verification'}
                  </div>
                </div>

                {/* completion mini bar */}
                <div className="completion-row" style={{ marginTop: 20 }}>
                  <FontAwesomeIcon icon={faArrowUp} style={{ color: 'var(--p-teal)', fontSize: '.8rem' }} />
                  <div className="completion-row__bar-wrap">
                    <div className="completion-row__bar" style={{ width: `${completionPct}%` }} />
                  </div>
                  <span className="completion-row__pct">{completionPct}%</span>
                  <span style={{ fontSize: '.75rem', color: 'var(--p-muted)', whiteSpace: 'nowrap' }}>Profile Complete</span>
                </div>
              </div>

              {/* Address card */}
              <div className="profile-card">
                <div className="profile-card__header">
                  <div className="profile-card__icon profile-card__icon--amber">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </div>
                  <div>
                    <p className="profile-card__title">Address Information</p>
                    <p className="profile-card__sub">Your shipping and billing address</p>
                  </div>
                </div>
                <div className="profile-grid-2">
                  <div className="col-span-2">
                    <Field label="Street Address" name="address.street" value={form.address.street} onChange={handleChange} disabled={!isEditing} required icon={faMapMarkerAlt} />
                  </div>
                  <Field label="City" name="address.city" value={form.address.city} onChange={handleChange} disabled={!isEditing} required icon={faMapMarkerAlt} />
                  <Field label="State" name="address.state" value={form.address.state} onChange={handleChange} disabled={!isEditing} required icon={faMapMarkerAlt} />
                  <Field label="ZIP / Postal Code" name="address.zipCode" value={form.address.zipCode} onChange={handleChange} disabled={!isEditing} icon={faMapMarkerAlt} />
                  <Field label="Country" name="address.country" value={form.address.country} onChange={handleChange} disabled={!isEditing} required icon={faMapMarkerAlt} />
                </div>
              </div>
            </>
          )}

          {/* ── ORDERS TAB ── */}
          {activeTab === 'orders' && (
            <>
              <div className="section-head">
                <div className="section-head__left">
                  <span className="section-head__tag">History</span>
                  <h2 className="section-head__title">Order History</h2>
                  <div className="section-head__line" />
                </div>
              </div>
              <div className="profile-card">
                {orders.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state__icon"><FontAwesomeIcon icon={faBox} /></div>
                    <p className="empty-state__title">No orders yet</p>
                    <p className="empty-state__sub">Your order history will appear here once you place an order.</p>
                  </div>
                ) : orders.map(o => (
                  <div key={o._id} className="list-row">
                    <div>
                      <p style={{ fontSize: '.72rem', color: '#9ca3af' }}>Order #{o._id}</p>
                      <p style={{ fontWeight: 500, marginTop: 4 }}>{o.items?.length} item(s)</p>
                      <p style={{ fontSize: '.82rem', color: '#6b7280', marginTop: 2 }}>Total: ${o.totalAmount}</p>
                    </div>
                    <span className={`chip ${o.status === 'delivered' ? 'chip--green' : o.status === 'processing' ? 'chip--blue' : 'chip--amber'}`}>
                      {o.status}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── QUERIES TAB ── */}
          {activeTab === 'queries' && (
            <>
              <div className="section-head">
                <div className="section-head__left">
                  <span className="section-head__tag">Support</span>
                  <h2 className="section-head__title">My Queries</h2>
                  <div className="section-head__line" />
                </div>
              </div>
              <div className="profile-card">
                {queries.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state__icon"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <p className="empty-state__title">No queries yet</p>
                    <p className="empty-state__sub">Queries you submit to our team will appear here.</p>
                  </div>
                ) : queries.map(q => (
                  <div key={q._id} className="list-row">
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '.72rem', color: '#9ca3af' }}>Query #{q._id}</p>
                      <p style={{ fontWeight: 500, marginTop: 4 }}>{q.subject}</p>
                      <p style={{ fontSize: '.82rem', color: '#6b7280', marginTop: 2 }}>{q.message}</p>
                      {q.response && (
                        <div style={{ marginTop: 12, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, borderLeft: '3px solid var(--p-teal)' }}>
                          <p style={{ fontSize: '.75rem', color: 'var(--p-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>Response</p>
                          <p style={{ fontSize: '.83rem', color: '#374151' }}>{q.response}</p>
                        </div>
                      )}
                    </div>
                    <span className={`chip ${q.status === 'resolved' ? 'chip--green' : q.status === 'pending' ? 'chip--amber' : 'chip--blue'}`}>
                      {q.status}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Profile;